import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { CardContent, CardHeader, CardTitle } from './ui/card';
import { CollapsibleCard } from './collapsible-card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import {
  Settings,
  ChevronLeft,
  ChevronRight,
  Search,
  Download,
} from 'lucide-react';
import {
  getOrderTypeDisplayName,
  getProviderDisplayNameWithBrand,
} from './lib/ordering';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const ROW_HEIGHT = 60; // Height of each row in pixels
const VISIBLE_ROWS = 10; // Number of rows to show in the visible area

export function VirtualizedOrderTable({
  data,
  serverPagination,
  onPageChange,
  onExportAll,
  isPageLoading = false,
}) {
  const [localPageSize, setLocalPageSize] = useState(25);
  const [localCurrentPage, setLocalCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // When serverPagination is given, `data.orders` is already the requested page
  // and the page state belongs to whoever fetched it. Search then only sees the
  // rows on screen, and the CSV export goes back to the server for the rest.
  const isServerPaged = Boolean(serverPagination);
  const pageSize = isServerPaged ? serverPagination.pageSize : localPageSize;
  const currentPage = isServerPaged
    ? serverPagination.currentPage
    : localCurrentPage;

  const formatReportPeriod = () => {
    if (!data.dateRange) return 'N/A';

    const formatDateTimeForPeriod = (dateStr, timeStr = '00:00') => {
      if (!dateStr) return 'N/A';

      // Parse date (YYYY-MM-DD)
      const [year, month, day] = dateStr.split('-').map(Number);

      // Parse time (HH:MM)
      const [hours, minutes] = timeStr.split(':').map(Number);
      const seconds = 0; // Form only provides HH:MM, so seconds default to 0

      // Create date object
      const date = new Date(year, month - 1, day, hours, minutes, seconds);

      // Format as MM/DD/YYYY, HH:MM:SS AM/PM (no leading zero on hour)
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const yearStr = date.getFullYear();

      let hour12 = date.getHours();
      const ampm = hour12 >= 12 ? 'PM' : 'AM';
      hour12 = hour12 % 12 || 12;
      // No padding for hour - keep as is (1-12)
      const hourStr = String(hour12);
      const minStr = String(date.getMinutes()).padStart(2, '0');
      const secStr = String(date.getSeconds()).padStart(2, '0');

      return `${monthStr}/${dayStr}/${yearStr}, ${hourStr}:${minStr}:${secStr} ${ampm}`;
    };

    const startDateTime = formatDateTimeForPeriod(
      data.dateRange.startDate,
      data.dateRange.startTime || '00:00'
    );
    const endDateTime = formatDateTimeForPeriod(
      data.dateRange.endDate,
      data.dateRange.endTime || '23:59'
    );

    return `${startDateTime} - ${endDateTime}`;
  };

  const reportPeriod = formatReportPeriod();

  const [columns, setColumns] = useState(() => [
    {
      id: 'orderNumber',
      label: 'Order #',
      width: 120,
      visible: true,
      align: 'left',
      accessor: (item) => item.order.displayNumber,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className="font-medium">{value}</span>
        </div>
      ),
    },
    {
      id: 'employee',
      label: 'Emp.',
      width: 80,
      visible: true,
      align: 'left',
      accessor: (item) => item.order.employeeInfo?.initials || '-',
      render: (value) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span className="text-sm font-medium">{value}</span>
        </div>
      ),
    },
    {
      id: 'employeeDetails',
      label: 'Employee Details',
      width: 200,
      visible: false,
      align: 'left',
      accessor: (item) => {
        const firstName = item.order.employeeInfo?.firstName || '';
        const lastName = item.order.employeeInfo?.lastName || '';
        if (!firstName && !lastName) return '-';
        return `${firstName} ${lastName}`.trim();
      },
      render: (value) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <span className="text-sm">{value}</span>
        </div>
      ),
    },
    {
      id: 'date',
      label: 'Date',
      width: 100,
      visible: true,
      align: 'left',
      accessor: (item) => {
        // Try to use pre-formatted dateOnly first, fallback to formatting placedAt
        if (item.dateOnly) {
          return item.dateOnly;
        }
        try {
          const date = new Date(item.order.placedAt);
          if (isNaN(date.getTime())) {
            return item.order.placedAt;
          }
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit',
          });
        } catch (error) {
          return item.order.placedAt;
        }
      },
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span>{value}</span>
        </div>
      ),
    },
    {
      id: 'time',
      label: 'Time',
      width: 100,
      visible: true,
      align: 'left',
      accessor: (item) => {
        console.log(item);
        // Try to use pre-formatted timeOnly first, fallback to formatting placedAt
        if (item.timeOnly) {
          return item.timeOnly;
        }
        try {
          const date = new Date(item.order.placedAt);
          if (isNaN(date.getTime())) {
            return '';
          }
          return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        } catch (error) {
          return '';
        }
      },
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className="text-muted-foreground text-sm">{value}</span>
        </div>
      ),
    },
    {
      id: 'orderType',
      label: 'Type',
      width: 200,
      visible: true,
      align: 'left',
      accessor: (item) => item.order.orderType,
      render: (value) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Badge variant="secondary" className={getOrderTypeColor(value)}>
            {getOrderTypeDisplayName(value)}
          </Badge>
        </div>
      ),
    },
    {
      id: 'provider',
      label: 'Provider',
      width: 160,
      visible: true,
      align: 'left',
      accessor: (item) =>
        getProviderDisplayNameWithBrand(
          item.order.provider,
          item.order.providerBrand
        ),
      render: (value) => (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Badge variant="outline">{value}</Badge>
        </div>
      ),
    },
    {
      id: 'subtotal',
      label: 'Subtotal',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.subTotal,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'adjustmentsAndFees',
      label: 'Adjustments and Fees',
      width: 180,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.adjustmentsAndFees ?? 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'regularDiscount',
      label: 'Store Discounts',
      width: 150,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.regularDiscountTotal ?? 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {value > 0 ? '-' : ''}
            {formatCurrency(Math.abs(value))}
          </span>
        </div>
      ),
    },
    {
      id: 'cashDiscount',
      label: 'Cash Discounts',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.cashDiscountTotal ?? 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {value > 0 ? '-' : ''}
            {formatCurrency(Math.abs(value))}
          </span>
        </div>
      ),
    },
    {
      id: 'coupon',
      label: 'Coupons',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => {
        console.log(item);
        return item.order.charges.couponTotal ?? 0;
      },
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {value > 0 ? '-' : ''}
            {formatCurrency(Math.abs(value))}
          </span>
        </div>
      ),
    },
    {
      id: 'promotion',
      label: 'Promotions',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.promotionTotal ?? 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {value > 0 ? '-' : ''}
            {formatCurrency(Math.abs(value))}
          </span>
        </div>
      ),
    },
    {
      id: 'reward',
      label: 'Rewards Used',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => {
        console.log(item);
        return (
          item.order.charges.rewardAmount + item.rewardsInfo.usedRewardsAmount
        );
      },
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {value > 0 ? '-' : ''}
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      id: 'tax',
      label: 'Tax',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.taxAmount,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'surcharge',
      label: 'Surcharge',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.surchargeAmount ?? 0,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'delivery',
      label: 'Delivery Fee',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.deliveryFee,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'deliveryCost',
      label: 'Delivery Fee Cost',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.deliveryCost,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'deliveryCostPaid',
      label: 'Delivery Fee Facilitated',
      width: 200,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.deliveryCostPaid,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>{formatCurrency(value)}</div>
      ),
    },
    {
      id: 'kitchenTip',
      label: 'Kitchen Tip',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.tipInfo.kitchen,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-blue-600' : ''}>
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      id: 'driverTip',
      label: 'Driver Tip',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.tipInfo.driver,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-blue-600' : ''}>
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      id: 'driverTipCost',
      label: 'Driver Tip Cost',
      width: 140,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.tipInfo.driverCost,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-blue-600' : ''}>
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      id: 'driverTipPaid',
      label: 'Driver Tip Facilitated',
      width: 180,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.tipInfo.driverPaid,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-blue-600' : ''}>
            {formatCurrency(value)}
          </span>
        </div>
      ),
    },
    {
      id: 'total',
      label: 'Total',
      width: 120,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.total,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className="font-medium">{formatCurrency(value)}</span>
        </div>
      ),
    },
    {
      id: 'refund',
      label: 'Refund',
      width: 100,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.refundAmount,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className={value > 0 ? 'text-red-600' : ''}>
            {formatCurrency(-Math.abs(value))}
          </span>
        </div>
      ),
    },
    {
      id: 'Unpaid Amount',
      label: 'Unpaid Amount',
      width: 160,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.balanceDue,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className="font-medium">{formatCurrency(value)}</span>
        </div>
      ),
    },
    {
      id: 'Amount Collected',
      label: 'Amount Collected',
      width: 160,
      visible: true,
      align: 'right',
      accessor: (item) => item.order.charges.amountCollected,
      render: (value) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <span className="font-medium">{formatCurrency(value)}</span>
        </div>
      ),
    },
  ]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data.orders;

    const searchLower = searchTerm.toLowerCase();
    return data.orders.filter((orderItem) => {
      const order = orderItem.order;

      // Use pre-formatted date/time if available, otherwise format from placedAt
      let dateStr = '';
      let timeStr = '';

      if (orderItem.dateOnly && orderItem.timeOnly) {
        dateStr = orderItem.dateOnly.toLowerCase();
        timeStr = orderItem.timeOnly.toLowerCase();
      } else {
        try {
          const date = new Date(order.placedAt);
          if (!isNaN(date.getTime())) {
            dateStr = date.toLocaleDateString('en-US').toLowerCase();
            timeStr = date.toLocaleTimeString('en-US').toLowerCase();
          }
        } catch (error) {
          // Ignore date parsing errors for search
        }
      }

      // Include employee info in search
      const employeeInitials =
        order.employeeInfo?.initials?.toLowerCase() || '';
      const employeeFirstName =
        order.employeeInfo?.firstName?.toLowerCase() || '';
      const employeeLastName =
        order.employeeInfo?.lastName?.toLowerCase() || '';

      return (
        order.displayNumber?.toString().toLowerCase().includes(searchLower) ||
        order.orderType?.toLowerCase().includes(searchLower) ||
        order.provider?.toLowerCase().includes(searchLower) ||
        getProviderDisplayNameWithBrand(order.provider, order.providerBrand)
          ?.toLowerCase()
          .includes(searchLower) ||
        dateStr.includes(searchLower) ||
        timeStr.includes(searchLower) ||
        employeeInitials.includes(searchLower) ||
        employeeFirstName.includes(searchLower) ||
        employeeLastName.includes(searchLower)
      );
    });
  }, [data.orders, searchTerm]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (isServerPaged) return filteredData;
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize, isServerPaged]);

  const totalPages = isServerPaged
    ? serverPagination.totalPages
    : Math.ceil(filteredData.length / pageSize);
  const totalItems = isServerPaged
    ? serverPagination.totalItems
    : filteredData.length;
  const visibleColumns = columns.filter((col) => col.visible);

  // Create parent ref for virtualizer
  const parentRef = useRef(null);

  // Virtualizer for rows
  const rowVirtualizer = useVirtualizer({
    count: paginatedData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
  });

  const toggleColumnVisibility = useCallback((columnId) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    );
  }, []);

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      const size = parseInt(newPageSize);
      if (isServerPaged) {
        onPageChange(0, size); // A different page size starts over at page one
        return;
      }
      setLocalPageSize(size);
      setLocalCurrentPage(0);
    },
    [isServerPaged, onPageChange]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      const page = Math.max(0, Math.min(newPage, totalPages - 1));
      if (isServerPaged) {
        onPageChange(page, pageSize);
        return;
      }
      setLocalCurrentPage(page);
    },
    [totalPages, isServerPaged, onPageChange, pageSize]
  );

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setLocalCurrentPage(0); // Reset to first page when searching
  }, []);

  const handleExportCSV = useCallback(async () => {
    // Get visible columns
    const visibleColumns = columns.filter((col) => col.visible);

    // Create CSV header
    const headers = visibleColumns.map((col) => col.label).join(',');

    // Only one page is in memory when the server does the paging, so the export
    // asks for the whole range. onExportAll returns null if that request failed,
    // having already told the user; there is nothing to write in that case.
    let exportData = filteredData;
    if (isServerPaged) {
      setIsExporting(true);
      try {
        exportData = await onExportAll();
      } finally {
        setIsExporting(false);
      }
      if (!exportData) return;
    }

    // Create CSV rows
    const rows = exportData.map((orderItem) => {
      return visibleColumns
        .map((col) => {
          let value = col.accessor(orderItem);

          // Format the value based on column type
          if (col.id === 'orderType') {
            value = getOrderTypeDisplayName(value);
          } else if (typeof value === 'number') {
            // Format numbers with 2 decimal places for currency
            value = value.toFixed(2);
          }

          // Escape commas and quotes in CSV
          if (typeof value === 'string') {
            value = value.replace(/"/g, '""');
            if (
              value.includes(',') ||
              value.includes('"') ||
              value.includes('\n')
            ) {
              value = `"${value}"`;
            }
          }

          return value || '';
        })
        .join(',');
    });

    // Combine header and rows
    const csv = [headers, ...rows].join('\n');

    // Create and trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `detailed-orders-${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [columns, filteredData, isServerPaged, onExportAll]);

  // Reset pagination when data changes (e.g., when filters change)
  useEffect(() => {
    setLocalCurrentPage(0);
  }, [data.orders]);

  return (
    <CollapsibleCard>
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Detailed Order Breakdown</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Report Period: {reportPeriod}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={
                  isServerPaged ? 'Search this page...' : 'Search orders...'
                }
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8 w-full"
              />
            </div>

            {/* Column Visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.visible}
                    onCheckedChange={() => toggleColumnVisibility(column.id)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Export to CSV */}
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              onClick={handleExportCSV}
              disabled={isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Preparing…' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Table Container with Horizontal Scroll */}
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <div
              className="relative"
              style={{
                minWidth: `${visibleColumns.reduce(
                  (sum, col) => sum + col.width,
                  0
                )}px`,
              }}
            >
              {/* Fixed Header */}
              <div className="border-b bg-muted/50 sticky top-0 z-10">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.map((column) => (
                        <TableHead
                          key={column.id}
                          style={{
                            width: column.width,
                            minWidth: column.width,
                            maxWidth: column.width,
                            whiteSpace: 'nowrap',
                            padding: '8px 12px',
                          }}
                          className={
                            column.align === 'right'
                              ? 'text-right'
                              : 'text-left'
                          }
                        >
                          {column.label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                </Table>
              </div>

              {/* Virtualized Body */}
              <div
                ref={parentRef}
                className="overflow-y-auto"
                style={{
                  height: `${VISIBLE_ROWS * ROW_HEIGHT}px`,
                }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const orderItem = paginatedData[virtualRow.index];
                    if (!orderItem) return null;

                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <Table>
                          <TableBody>
                            <TableRow
                              className={`hover:bg-muted/70 transition-colors ${
                                virtualRow.index % 2 === 0
                                  ? 'bg-background'
                                  : 'bg-muted/20'
                              }`}
                              style={{ height: `${virtualRow.size}px` }}
                            >
                              {visibleColumns.map((column) => {
                                const value = column.accessor(orderItem);
                                const content = column.render
                                  ? column.render(value, orderItem)
                                  : value;

                                return (
                                  <TableCell
                                    key={column.id}
                                    style={{
                                      width: column.width,
                                      minWidth: column.width,
                                      maxWidth: column.width,
                                      padding: '8px 12px',
                                    }}
                                    className={
                                      column.align === 'right'
                                        ? 'text-right'
                                        : 'text-left'
                                    }
                                  >
                                    {content}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-4">
          <div className="text-sm text-muted-foreground">
            {totalItems === 0
              ? 'No orders'
              : `Showing ${currentPage * pageSize + 1} to ${
                  currentPage * pageSize + paginatedData.length
                } of ${totalItems} orders`}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-sm">Rows per page:</span>
              <Select
                value={pageSize.toString()}
                onValueChange={handlePageSizeChange}
                disabled={isPageLoading}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center gap-1 w-full sm:w-auto justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0 || isPageLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="text-sm px-2">
                {isPageLoading
                  ? 'Loading…'
                  : `Page ${currentPage + 1} of ${totalPages}`}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1 || isPageLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>
              🚀 Virtualized: Rendering{' '}
              {rowVirtualizer.getVirtualItems().length} of{' '}
              {paginatedData.length} rows
            </span>
            <span>
              📊 {visibleColumns.length} of {columns.length} columns visible
            </span>
            <span>
              🔍 {filteredData.length} of {data.orders.length} orders match
              filter{isServerPaged ? ' on this page' : ''}
            </span>
          </div>
        </div>
      </CardContent>
    </CollapsibleCard>
  );
}

// Helper functions
function formatCurrency(amount) {
  const normalized = Object.is(amount, -0) ? 0 : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(normalized);
}

function getOrderTypeColor(type) {
  type = type || 'other';
  switch (type.toLowerCase()) {
    case 'delivery':
      return 'bg-purple-100 text-purple-800';
    case 'pickup':
      return 'bg-orange-100 text-orange-800';
    case 'dine-in':
    case 'in_store':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
